import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import classes from '@nerdwallet/base-styles/classes';
import Type from '@nerdwallet/react-typography';
import Link from '@nerdwallet/react-link';
import Twitter from '@nerdwallet/react-icon/social-regular/Twitter';
import Mail from '@nerdwallet/react-icon/social-regular/Mail';
import Facebook from '@nerdwallet/react-icon/social-regular/Facebook';

interface Author {
  name: string;
  heading: string;
  bio: string;
  fullPageUrl: string;
  socialMediaLinks: Array<{
    url: string;
    type: string;
  }>;
}

interface Props extends Author {
  className: string;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- directive added automatically by Shepherd migration
const AuthorCard = ({
  name,
  heading,
  bio,
  fullPageUrl,
  socialMediaLinks,
  className,
}: Props) => {
  if (!name) {
    return null;
  }

  const getSocialMediaLinks = () => {
    const validSocialMediaLinks =
      socialMediaLinks &&
      socialMediaLinks.filter((linkProps) => linkProps.type && linkProps.url);

    const socialIcon = (type: string) => {
      if (type === 'twitter') {
        return <Twitter textAligned />;
      }
      if (type === 'email') {
        return <Mail textAligned />;
      }
      if (type === 'facebook') {
        return <Facebook textAligned />;
      }
      return null;
    };

    return (
      validSocialMediaLinks &&
      validSocialMediaLinks.length > 0 && (
        <div>
          {validSocialMediaLinks.map((linkProps) => (
            <div
              key={linkProps.type}
              className={classes([
                'display-inline-block',
                'nowrap',
                'margin-right-3',
              ])}
            >
              <Link
                href={linkProps.url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="author-card__social-icons"
                unstyled
              >
                <span className={classes('vertical-align-middle')}>
                  {socialIcon(linkProps.type)}
                </span>
              </Link>
            </div>
          ))}
        </div>
      )
    );
  };

  return (
    <div
      className={classNames([
        'author-card',
        className,
        classes(['display-flex', 'flex-direction-column']),
      ])}
    >
      <div className={classes('padding-bottom-1')}>
        {heading && (
          <Type size={1} bold inline>
            {heading}{' '}
          </Type>
        )}
        {bio && (
          <Type size={1} className="author-card__bio" inline italic>
            {bio}
            {fullPageUrl && (
              <Link
                href={fullPageUrl}
                className={classNames([classes(['nowrap'])])}
              >
                {' '}
                Read more
              </Link>
            )}
          </Type>
        )}
      </div>
      {getSocialMediaLinks()}
    </div>
  );
};

AuthorCard.displayName = 'nw-author-card';

AuthorCard.propTypes = {
  name: PropTypes.string.isRequired,
  heading: PropTypes.string,
  bio: PropTypes.string,
  fullPageUrl: PropTypes.string,
  socialMediaLinks: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      name: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  className: PropTypes.string,
};

export default AuthorCard;
